import { useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  CheckCircle2,
  Circle,
  Mic,
  Pause,
  Play,
  ShieldCheck,
  Sparkles,
  Volume2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  ClinicalQuestion,
  SurgeonConsoleProofPacket,
  revenueOpportunityToQuestion,
  validateConversationAnswer,
} from '@/lib/surgeon-console/proofPacket';
import { useSpeechSynthesis } from '@/hooks/useSpeechSynthesis';

type BrowserSpeechRecognitionEvent = {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: {
      isFinal: boolean;
      0: { transcript: string };
    };
  };
};

interface SurgeonConversationPanelProps {
  packet: SurgeonConsoleProofPacket;
  onAcceptStatement: (statement: string, question: ClinicalQuestion) => void;
  acceptedStatements?: string[];
  className?: string;
}

function getStatusCopy(packet: SurgeonConsoleProofPacket): string {
  if (packet.readinessStatus === 'ready') return 'Ready';
  if (packet.readinessStatus === 'needs_surgeon') return 'Needs surgeon input';
  if (packet.readinessStatus === 'needs_staff') return 'Needs staff evidence';
  return 'Blocked';
}

function getStatusClass(packet: SurgeonConsoleProofPacket): string {
  if (packet.readinessStatus === 'ready') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (packet.readinessStatus === 'needs_surgeon') return 'border-amber-200 bg-amber-50 text-amber-800';
  if (packet.readinessStatus === 'needs_staff') return 'border-sky-200 bg-sky-50 text-sky-800';
  return 'border-destructive/30 bg-destructive/10 text-destructive';
}

export function SurgeonConversationPanel({
  packet,
  onAcceptStatement,
  acceptedStatements = [],
  className,
}: SurgeonConversationPanelProps) {
  const { isSpeaking, handleSpeak } = useSpeechSynthesis();
  const allQuestions = useMemo(() => [
    ...packet.questions,
    ...packet.revenueOpportunities.map(opportunity =>
      revenueOpportunityToQuestion(opportunity, packet.mode),
    ),
  ], [packet]);

  const [activeQuestionId, setActiveQuestionId] = useState(allQuestions[0]?.id || '');
  const [draft, setDraft] = useState(allQuestions[0]?.suggestedLanguage || '');
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<any>(null);

  const activeQuestion = allQuestions.find(question => question.id === activeQuestionId) || allQuestions[0] || null;
  const validation = activeQuestion ? validateConversationAnswer(activeQuestion, draft) : [];
  const satisfiedCount = validation.filter(item => item.satisfied).length;
  const allSatisfied = validation.length === 0 || satisfiedCount === validation.length;
  const acceptedSet = new Set(acceptedStatements.map(statement => statement.trim().toLowerCase()));
  const activeSuggestedLanguage = activeQuestion?.suggestedLanguage.trim().toLowerCase() || '';
  const activeSuggestionAccepted = activeSuggestedLanguage ? acceptedSet.has(activeSuggestedLanguage) : false;

  const selectQuestion = (question: ClinicalQuestion) => {
    setActiveQuestionId(question.id);
    setDraft(question.suggestedLanguage);
  };

  const handleUseVerbatim = () => {
    if (!activeQuestion) return;
    setDraft(activeQuestion.suggestedLanguage);
    onAcceptStatement(activeQuestion.suggestedLanguage, activeQuestion);
    toast.success('Surgeon statement added', {
      description: activeQuestion.severity === 'revenue'
        ? 'Revenue integrity language will be included if clinically true.'
        : 'The payer-ready attestation was added to the packet.',
    });
  };

  const handleAddDraft = () => {
    if (!activeQuestion || !draft.trim()) return;
    onAcceptStatement(draft.trim(), activeQuestion);
    toast.success('Dictated statement added', {
      description: allSatisfied
        ? 'Live criteria are satisfied.'
        : 'Statement added. Remaining criteria stay visible for review.',
    });
  };

  const toggleDictation = () => {
    if (isRecording) {
      recognitionRef.current?.stop?.();
      setIsRecording(false);
      return;
    }

    const SpeechRecognitionAPI = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      toast.error('Dictation is not available in this browser');
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    let finalTranscript = draft ? `${draft.trim()} ` : '';

    recognition.onresult = (event: BrowserSpeechRecognitionEvent) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += `${result[0].transcript} `;
        } else {
          interim += result[0].transcript;
        }
      }
      setDraft(`${finalTranscript}${interim}`.trim());
    };
    recognition.onerror = () => setIsRecording(false);
    recognition.onend = () => setIsRecording(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  };

  const title = packet.mode === 'post-op'
    ? 'Walkout Conversation'
    : 'Surgeon Conversation';
  const subtitle = packet.mode === 'post-op'
    ? 'A natural interview that turns the surgeon walkout into a protected op note.'
    : 'The system asks only what the packet still needs, then validates the answer in real time.';

  return (
    <Card className={cn('border-primary/20 bg-primary/[0.025]', className)}>
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">{title}</h3>
                <p className="text-xs text-muted-foreground">{subtitle}</p>
              </div>
            </div>
          </div>
          <Badge variant="outline" className={cn('w-fit border font-semibold', getStatusClass(packet))}>
            {getStatusCopy(packet)}
          </Badge>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.4fr] gap-4">
          <div className="space-y-3">
            <div className="rounded-lg border bg-card p-3 space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                <ShieldCheck className="w-3.5 h-3.5" />
                Proof Readiness
              </div>
              <div className="space-y-2">
                {packet.criteria.map(item => (
                  <div key={item.id} className="flex items-start gap-2 text-xs">
                    {item.status === 'satisfied' ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{item.label}</p>
                      <p className="text-muted-foreground leading-snug">{item.explanation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {allQuestions.length > 0 && (
              <div className="rounded-lg border bg-card p-2 space-y-1">
                {allQuestions.map(question => {
                  const isAccepted = acceptedSet.has(question.suggestedLanguage.trim().toLowerCase());
                  return (
                    <button
                      key={question.id}
                      type="button"
                      onClick={() => selectQuestion(question)}
                      className={cn(
                        'w-full rounded-md px-3 py-2 text-left text-xs transition-colors',
                        activeQuestion?.id === question.id
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-muted text-muted-foreground',
                      )}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="font-semibold">{question.title}</span>
                        {question.severity === 'revenue' && (
                          <Badge variant="outline" className="text-[9px] border-emerald-200 text-emerald-700 bg-emerald-50">
                            Revenue
                          </Badge>
                        )}
                        {isAccepted && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-xl border bg-card p-4 space-y-4">
            {activeQuestion ? (
              <>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Badge variant="outline" className="text-[10px]">
                      {activeQuestion.severity === 'revenue' ? 'Revenue Integrity' : 'Clinical Proof'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs gap-1.5"
                      onClick={() => handleSpeak(activeQuestion.spokenPrompt)}
                    >
                      <Volume2 className="w-3.5 h-3.5" />
                      {isSpeaking ? 'Stop' : 'Read Aloud'}
                    </Button>
                  </div>
                  <p className="text-sm font-semibold text-foreground leading-relaxed">
                    {activeQuestion.spokenPrompt}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {activeQuestion.payerCriterion}
                  </p>
                </div>

                <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-emerald-900">Payer-ready language</p>
                    <Button
                      size="sm"
                      className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleUseVerbatim}
                      disabled={activeSuggestionAccepted}
                    >
                      {activeSuggestionAccepted ? 'Added' : 'Use & Close Gap'}
                    </Button>
                  </div>
                  <p className="text-sm leading-relaxed text-emerald-950">
                    {activeQuestion.suggestedLanguage}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Dictate or edit
                    </p>
                    <Button
                      variant={isRecording ? 'destructive' : 'outline'}
                      size="sm"
                      className="h-8 text-xs gap-1.5"
                      onClick={toggleDictation}
                    >
                      {isRecording ? <Pause className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                      {isRecording ? 'Stop' : 'Dictate'}
                    </Button>
                  </div>
                  <Textarea
                    value={draft}
                    onChange={event => setDraft(event.target.value)}
                    placeholder={activeQuestion.answerPlaceholder}
                    className="min-h-[112px] resize-y bg-background"
                  />
                </div>

                <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Live criteria check
                    </p>
                    <Badge variant="outline" className={cn(
                      'text-[10px]',
                      allSatisfied
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-amber-200 bg-amber-50 text-amber-700',
                    )}>
                      {satisfiedCount}/{validation.length || 0} satisfied
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {validation.map(item => (
                      <div key={item.requirementId} className="flex items-center gap-2 text-xs">
                        {item.satisfied ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Circle className="w-3.5 h-3.5 text-amber-500" />
                        )}
                        <span className={item.satisfied ? 'text-foreground' : 'text-muted-foreground'}>
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDraft(activeQuestion.suggestedLanguage)}>
                    <Play className="w-3.5 h-3.5" />
                    Reset Suggestion
                  </Button>
                  <Button size="sm" onClick={handleAddDraft} disabled={!draft.trim()}>
                    Add to Packet
                  </Button>
                </div>
              </>
            ) : (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                No surgeon questions are needed right now. Denial Defender criteria and revenue checks are supported by the current packet.
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
